import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_STYLES } from "@/lib/session";

const emptyForm = { category: CATEGORIES[0], subcategory: "", title: "", body: "", default_duration_minutes: 5 };

export default function LibraryAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadItems = () => {
    api.get("/library").then(({ data }) => setItems(data));
  };

  useEffect(() => {
    loadItems();
  }, []);

  if (user && user.role !== "admin") {
    return <Navigate to="/sessions" replace />;
  }

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({ category: item.category, subcategory: item.subcategory || "", title: item.title, body: item.body, default_duration_minutes: item.default_duration_minutes });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/library/${editingItem.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/library", form);
        toast.success("Item added to library");
      }
      setDialogOpen(false);
      loadItems();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from the shared library?`)) return;
    await api.delete(`/library/${item.id}`);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    toast.success("Item deleted");
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100">Manage Library</h1>
            <p className="text-sm text-muted-foreground mt-1">Add, edit, or remove official prayers, chants, hymns, and activities</p>
          </div>
          <Button onClick={openCreate} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="add-library-item-button">
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>

        <div className="space-y-6" data-testid="library-admin-list">
          {CATEGORIES.map((cat) => {
            const catItems = items.filter((i) => i.category === cat);
            const style = CATEGORY_STYLES[cat];
            return (
              <div key={cat} className="rounded-lg border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] overflow-hidden">
                <div className={`px-4 py-2 font-display text-lg font-semibold ${style.header}`}>{cat} ({catItems.length})</div>
                <div className="divide-y divide-[var(--brand-border)] dark:divide-[var(--brand-border-dark)] bg-white dark:bg-[var(--brand-card-dark)]">
                  {catItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between px-4 py-3" data-testid={`library-admin-item-${item.id}`}>
                      <div>
                        <p className="text-sm font-semibold">{item.title} {item.subcategory && <span className="text-xs text-muted-foreground font-normal">· {item.subcategory}</span>}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.body}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => openEdit(item)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-library-item-${item.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(item)} className="text-muted-foreground hover:text-[var(--brand-crimson)]" data-testid={`delete-library-item-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {catItems.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No items yet</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="library-item-form-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingItem ? "Edit item" : "Add new item"}</DialogTitle>
            <DialogDescription>This will {editingItem ? "update" : "add to"} the shared Prayer Library visible to all leaders.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="library-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subcategory</Label>
              <Input placeholder="e.g. Adoration, Basic Prayers..." value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="library-item-subcategory-input" />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="library-item-title-input" />
            </div>
            <div>
              <Label>Text</Label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="library-item-body-input" />
            </div>
            <div>
              <Label>Default duration (minutes)</Label>
              <Input type="number" min={1} value={form.default_duration_minutes} onChange={(e) => setForm({ ...form, default_duration_minutes: Number(e.target.value) || 1 })} data-testid="library-item-duration-input" />
            </div>
            <Button onClick={submit} disabled={saving || !form.title.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="library-item-submit-button">
              {saving ? "Saving..." : editingItem ? "Save changes" : "Add to library"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
